import React from 'react';
import { NOTI_TYPE } from '@typings/notification/notificationType';
import styled from 'styled-components';
import { Inotification } from '@typings/notification/notification.interface';
import { User } from '@typings/setting/userType.interface';
import extractTextToHtml from '@utils/extractTextToHtml';
import { getJosaGa } from '@utils/josa';

const Content = styled.div`
  width: 100%;
  text-align: left;
`;

const isNullLikeValue = (value?: string) => {
  if (!value) return true;
  const normalized = value.trim().toLowerCase();
  return normalized === 'null' || normalized === 'undefined';
};

/**
 * senderData 객체 또는 문자열 후보들에서 안전한 표시 이름을 반환한다.
 * - 객체 전달: senderData.displayName → senderData.id → '익명'
 * - 문자열 전달: 첫 번째 유효한 값 → '익명'
 */
export const getSafeSenderName = (...candidates: any[]): string => {
  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object') {
      const displayName = candidate.displayName;
      if (typeof displayName === 'string' && !isNullLikeValue(displayName)) {
        return displayName;
      }
      const id = candidate.id;
      if (typeof id === 'string' && !isNullLikeValue(id)) {
        return id;
      }
    } else if (typeof candidate === 'string' && !isNullLikeValue(candidate)) {
      return candidate.trim();
    }
  }
  return '익명';
};

export const BlockStyle = styled.div<{ isSeen?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  background: var(--inkColor);
  margin-top: 8px;
  position: relative;

  span {
    overflow: hidden;
    white-space: normal;
    text-overflow: ellipsis;
    word-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
`;

export const StrongStyle = styled.strong`
  max-width: 50%;
  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  word-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  float: left;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
    text-decoration: underline;
  }
`;

const DrawBox = ({
  senderData,
  affix,
  comment,
  isPresent,
  postingData,
  receiverData,
}: {
  senderData: any;
  affix?: string;
  comment?: string;
  isPresent?: boolean;
  receiverData?: any;
  postingData: {
    postingTitle: string;
    contents: string;
  };
}) => {
  const senderName = getSafeSenderName(senderData);

  return (
    <Content>
      {isPresent && receiverData ? (
        <>
          {postingData.postingTitle && (
            <StrongStyle
              dangerouslySetInnerHTML={{
                __html: extractTextToHtml(postingData.postingTitle),
              }}
            />
          )}
          <strong>{receiverData.displayName || ''}</strong>
        </>
      ) : (
        affix && (
          <StrongStyle
            dangerouslySetInnerHTML={{
              __html: extractTextToHtml(postingData.postingTitle),
            }}
          />
        )
      )}
      {affix} <strong>{senderName}</strong>
      <span>{getJosaGa(senderName)}</span>{' '}
      {comment}
      <BlockStyle>
        <span
          dangerouslySetInnerHTML={{
            __html: extractTextToHtml(postingData.contents),
          }}
        />
      </BlockStyle>
    </Content>
  );
};

type Props = {
  data: Inotification;
  senderData: User;
  receiverData: User | any;
  postData: any;
  numberId?: number;
};

/**
 * postData 없이도 senderData + data.contents만으로 기본 메시지를 렌더링하는 fallback.
 * 게시글이 삭제되었거나 postId가 null인 경우에도 알림 내용을 표시한다.
 */
function FallbackMessage({
  senderData,
  data,
}: {
  senderData: any;
  data: Inotification;
}) {
  const senderName = getSafeSenderName(senderData);

  const labelMap: Partial<Record<string, string>> = {
    [NOTI_TYPE.COMMENT]: '댓글을 달았습니다.',
    [NOTI_TYPE.REPLY]: '답글을 달았습니다.',
    [NOTI_TYPE.GUESTBOOK]: '방명록에 새 글을 남겼습니다.',
    [NOTI_TYPE.TAGGED]: '당신을 태그했습니다.',
    [NOTI_TYPE.PRESENT]: '선물을 보냈습니다.',
    [NOTI_TYPE.BATTLE]: '도전장을 보냈습니다.',
    [NOTI_TYPE.QUEST_REQUEST]: '퀘스트 검토를 요청했습니다.',
    [NOTI_TYPE.DISPATCH_COMPLETE]: '파견에서 돌아왔습니다.',
  };

  const label = labelMap[data.type] ?? '새 알림이 있습니다.';

  return (
    <Content>
      <strong>{senderName}</strong>
      <span>님이 {label}</span>
      {data.contents && (
        <BlockStyle>
          <span
            dangerouslySetInnerHTML={{
              __html: extractTextToHtml(data.contents),
            }}
          />
        </BlockStyle>
      )}
    </Content>
  );
}

function NotificationMessageMapper({
  postData,
  data,
  senderData,
  receiverData,
  numberId,
}: Props) {
  if (!senderData) return null;

  if (data.type === NOTI_TYPE.DISPATCH_COMPLETE) {
    return (
      <Content>
        <strong>{data.postTitle || '파견 완료'}</strong>
        {data.contents && (
          <BlockStyle>
            <span
              dangerouslySetInnerHTML={{
                __html: extractTextToHtml(data.contents),
              }}
            />
          </BlockStyle>
        )}
      </Content>
    );
  }

  if (data.type === NOTI_TYPE.QUEST_REQUEST) {
    return (
      <Content>
        <strong>{senderData.displayName || senderData.id}</strong>
        <span>님이 퀘스트 검토를 요청했습니다.</span>
        <BlockStyle>
          <span
            dangerouslySetInnerHTML={{
              __html: extractTextToHtml(data.contents || ''),
            }}
          />
        </BlockStyle>
      </Content>
    );
  }

  if (!postData) return <FallbackMessage senderData={senderData} data={data} />;

  const rawTitle = postData.title
    ? postData.title
    : postData.contents
      ? extractTextToHtml(postData.contents)
      : '';

  const postingTitle = numberId && numberId > 0
    ? `[No.${numberId}] ${rawTitle}`
    : rawTitle;

  switch (data.type) {
    case NOTI_TYPE.COMMENT: {
      return (
        <DrawBox
          senderData={senderData}
          affix={'에'}
          comment={'댓글을 달았습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    case NOTI_TYPE.REPLY: {
      return (
        <DrawBox
          senderData={senderData}
          affix={'에'}
          comment={'답글을 달았습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    case NOTI_TYPE.TAGGED: {
      return (
        <DrawBox
          senderData={senderData}
          affix={'에서'}
          comment={'당신을 태그했습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    case NOTI_TYPE.GUESTBOOK: {
      return (
        <DrawBox
          senderData={senderData}
          affix={''}
          comment={'방명록에 새 글을 남겼습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    case NOTI_TYPE.PRESENT: {
      return (
        <DrawBox
          senderData={senderData}
          affix={'에게'}
          comment={'선물을 보냈습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
          receiverData={receiverData}
          isPresent
        />
      );
    }

    case NOTI_TYPE.BATTLE: {
      return (
        <DrawBox
          senderData={senderData}
          affix={'에서'}
          comment={'도전장을 보냈습니다. ✊✌✋'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    default:
      return null;
  }
}

export default NotificationMessageMapper;
